import React, { useState, useMemo, useEffect } from "react";
import { Button, Dropdown, Menu } from "semantic-ui-react";
import {
  ViewType,
  AppointmentMeHey,
  formatThaiMonth,
  formatEnglishMonth,
  formatThaiYear,
} from "./AppointmentMeHeyUtils";
import AppointmentMonthViewMeHey from "./AppointmentMonthViewMeHey";
import { getAvailableSlotCount } from "./appointmentAvailability";
import AppointmentWeekViewMeHey from "./AppointmentWeekViewMeHey";
import AppointmentDayViewMeHey from "./AppointmentDayViewMeHey";
import AppointmentRightPanelMeHey from "./AppointmentRightPanelMeHey";
import ModalCreateAppointmentMeHey from "./ModalCreateAppointmentMeHey";
import TwoPanelSplitView from "./TwoPanelSplitView";
import PackageRightPanelMeHey from "./PackageRightPanelMeHey";
import { convertStringToColor } from "react-lib/apps/MEH/sequence/ScheduleSetting";
import { adStringToDateObject } from "react-lib/utils/dateUtils";
import "./CardMakeAppointmentMeHey.css";
import "./ModalCreateAppointmentMeHey.css";

// Centralized status mapping function
export const getAppointmentStatusInfo = (status: number): { text: string; className: string } => {
  switch (status) {
    case 1: // REQUEST - นัดหมายยังไม่ได้ถูกระบุ
      return { text: "รอยืนยัน", className: "appointment-status-pending" };
    case 2: // CONFIRM - นัดหมายถูกระบุและยืนยัน
      return { text: "ยืนยันนัดหมาย", className: "appointment-status-confirmed" };
    case 3: // REVISE - นัดหมายต้องการการคำนวณใหม่
      return { text: "ถูกเลื่อนนัด", className: "appointment-status-pending" };
    case 4: // RECONFIRM - นัดหมายต้องการยืนยันจากผู้ป่วยอีกครั้ง
      return { text: "รอยืนยัน", className: "appointment-status-pending" };
    case 5: // CANCEL - นัดหมายถูกยกเลิกจากผู้ป่วย
      return { text: "ยกเลิกนัดหมาย", className: "appointment-status-cancelled" };
    case 6: // REJECT - นัดหมายถูกยกเลิกจาก Provider
      return { text: "ปฏิเสธโดย Provider", className: "appointment-status-cancelled" };
    case 7: // ABANDON - ผู้ป่วยไม่มาตามนัด
      return { text: "ไม่มาตามนัด", className: "appointment-status-abandoned" };
    case 8: // FINISH - เสร็จสิ้น
      return { text: "เสร็จสิ้น", className: "appointment-status-finished" };
    default:
      return { text: "ไม่ทราบสถานะ", className: "appointment-status-unknown" };
  }
};

interface CardMakeAppointmentMeHeyProps {
  MakeAppointmentMeHeySequence: any;
  controller: any;
  runSequence: any;
  onEvent: any;
  setProp: any;
  languageUX: "en" | "th";
  onAppointmentClick?: (appointment: AppointmentMeHey) => void;
  onCreateAppointment?: (date: Date) => void;
  onTimeSlotSelect?: (appointment: AppointmentMeHey, hour: number) => void;
  onClose?: () => void;
  searchedItemListWithKey?: Record<string, any>;
  isAppointmentList?: boolean;
  isPackageAppointment?: boolean;
  disableWheelNavigation?: boolean;
  selectedPatient?: Record<string, any>;
  selectPackageBinder?: any;
  buttonLoadCheck?: any;
  onAppointmentSuccess?: any;
}

const CardMakeAppointmentMeHey: React.FC<CardMakeAppointmentMeHeyProps> = (
  props: CardMakeAppointmentMeHeyProps
) => {
  const [selectedDoctorDSB, setSelectedDoctorDSB] = useState<
    (AppointmentMeHey & { dsb?: any }) | null
  >(null);
  const [editingAppointment, setEditingAppointment] = useState<
    (AppointmentMeHey & { dsb?: any }) | null
  >(null);
  const [lastWheelTime, setLastWheelTime] = useState<number>(0);
  const [availabilityNow, setAvailabilityNow] = useState(() => new Date());

  useEffect(() => {
    if (!props.isPackageAppointment) return;
    const timer = window.setInterval(() => setAvailabilityNow(new Date()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, [props.isPackageAppointment]);
  const [sortField, setSortField] = useState<"time" | "status" | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    props.runSequence({ sequence: "MakeAppointmentMeHey", restart: true });
    return () => {
      props.runSequence({ sequence: "MakeAppointmentMeHey", clear: true });
    };
  }, []);

  useEffect(() => {
    const detail = props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail;

    if (detail) {
      setEditingAppointment({
        id: detail.id,
        estimated_at: detail.estimated_at_iso,
        order_note: detail.order_note,
        status: detail.status,
        tel: detail.patient_tel,
      });
    } else if (props.MakeAppointmentMeHeySequence?.showCreateModal) {
      setEditingAppointment(null);
    }
  }, [
    props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail,
    props.MakeAppointmentMeHeySequence?.showCreateModal,
  ]);

  const setCurrentDate = (date: Date) => {
    props.runSequence({
      sequence: "MakeAppointmentMeHey",
      action: "SET_CURRENT_DATE",
      currentDate: date,
    });
  };

  const setViewType = (view: "month" | "week" | "day") => {
    props.runSequence({
      sequence: "MakeAppointmentMeHey",
      action: "SET_VIEW_TYPE",
      viewType: view,
    });
  };

  // Get the light shade color for panel background
  const panelBackgroundColor = useMemo(() => {
    if (!selectedDoctorDSB || !selectedDoctorDSB.doctor) return "";

    const colors = convertStringToColor(selectedDoctorDSB.doctor);
    return colors.light;
  }, [selectedDoctorDSB]);

  // When switching to day view and there's a selected appointment, navigate to that appointment's date
  useEffect(() => {
    console.log("useEffect selectedAppointment: ", selectedDoctorDSB);
    if (props.MakeAppointmentMeHeySequence?.viewType === "day" && selectedDoctorDSB) {
      setCurrentDate(selectedDoctorDSB.startTime);
    }
  }, [props.MakeAppointmentMeHeySequence?.viewType, selectedDoctorDSB?.doctor_dsb_id]);

  const providersOptions = useMemo(() => {
    if (!props.MakeAppointmentMeHeySequence?.allProviders) return [];
    return props.MakeAppointmentMeHeySequence?.allProviders.map((provider: any) => ({
      key: provider.provider_id,
      text: `${provider.pre_name ? `${provider.pre_name} ` : ""}${
        provider.first_name ? `${provider.first_name} ` : ""
      } ${provider.last_name ? `${provider.last_name}` : ""}`,
      value: provider.provider_id,
    }));
  }, [props.MakeAppointmentMeHeySequence?.allProviders]);

  const handlePrevious = () => {
    const newDate = new Date(props.MakeAppointmentMeHeySequence?.currentDate);
    if (props.MakeAppointmentMeHeySequence?.viewType === "month") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (props.MakeAppointmentMeHeySequence?.viewType === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
    setSelectedDoctorDSB(null);
  };

  const handleNext = () => {
    const newDate = new Date(props.MakeAppointmentMeHeySequence?.currentDate);
    if (props.MakeAppointmentMeHeySequence?.viewType === "month") {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (props.MakeAppointmentMeHeySequence?.viewType === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
    setSelectedDoctorDSB(null);
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (props.disableWheelNavigation) {
      return;
    }

    if (props.MakeAppointmentMeHeySequence?.viewType !== "month") {
      return;
    }

    const target = event.target as HTMLElement;
    const calendarContent = target.closest(".calendar-content-month");

    if (!calendarContent) return;

    const now = Date.now();
    if (now - lastWheelTime < 800) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setLastWheelTime(now);

    if (event.deltaY > 0) {
      handleNext();
    } else if (event.deltaY < 0) {
      handlePrevious();
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDoctorDSB(null);
  };

  const handleDayClick = (date: Date) => {
    setCurrentDate(date);
    setSelectedDoctorDSB(null);

    if (props.onCreateAppointment) {
      props.onCreateAppointment(date);
    }
  };

  useEffect(() => {
    if (selectedDoctorDSB?.doctor_dsb_id && props.MakeAppointmentMeHeySequence?.dsb) {
      let id = selectedDoctorDSB.doctor_dsb_id;
      let dsb = props.MakeAppointmentMeHeySequence?.dsb
        ?.flatMap((dsbItem: any) => {
          return dsbItem.providers;
        })
        .find((providerItem: any) => {
          return providerItem.dsb.doctor_dsb_id === id;
        });

      let addDSB = { ...selectedDoctorDSB, dsb: dsb ? dsb.dsb : null };
      setSelectedDoctorDSB(addDSB);
    }
  }, [selectedDoctorDSB?.doctor_dsb_id, props.MakeAppointmentMeHeySequence?.dsb]);

  const handleAppointmentClick = (appointment: AppointmentMeHey) => {
    let id = appointment.doctor_dsb_id;
    let dsb = props.MakeAppointmentMeHeySequence?.dsb
      ?.flatMap((dsbItem: any) => {
        return dsbItem.providers;
      })
      .find((providerItem: any) => {
        return providerItem.dsb.doctor_dsb_id === id;
      });
    let addDSB = { ...appointment, dsb: dsb ? dsb.dsb : null };
    setSelectedDoctorDSB(addDSB);
    if (props.onAppointmentClick) {
      props.onAppointmentClick(addDSB);
    }
  };

  const handleMoreClick = (date: Date) => {
    setCurrentDate(date);
    setViewType("day");
    setSelectedDoctorDSB(null);
  };

  const handleSort = (field: "time" | "status") => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortField(null);
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleCloseCreateModal = () => {
    props.runSequence({
      sequence: "MakeAppointmentMeHey",
      action: "SET_SHOW_CREATE_MODAL",
      showCreateModal: false,
    });
    setEditingAppointment(null);

    props.onClose?.();
  };

  const handleTimeSlotSelect = (hour: number) => {
    if (selectedDoctorDSB && props.onTimeSlotSelect) {
      props.onTimeSlotSelect(selectedDoctorDSB, hour);
    }
  };

  const getDisplayMonth = () => {
    if (props.MakeAppointmentMeHeySequence?.viewType === "month") {
      return `${formatThaiMonth(
        props.MakeAppointmentMeHeySequence?.currentDate
      )} ${props.MakeAppointmentMeHeySequence?.currentDate?.getFullYear()}`;
    } else if (props.MakeAppointmentMeHeySequence?.viewType === "week") {
      const weekStart = new Date(props.MakeAppointmentMeHeySequence?.currentDate);
      const dayOfWeek = weekStart.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      weekStart.setDate(weekStart.getDate() + diff);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const monthName = formatEnglishMonth(weekStart);
      const startDay = weekStart.getDate();
      const endDay = weekEnd.getDate();
      const year = weekStart.getFullYear();

      return `${monthName} ${startDay}-${endDay}, ${year}`;
    } else {
      return `${props.MakeAppointmentMeHeySequence?.currentDate?.getDate()} ${formatThaiMonth(
        props.MakeAppointmentMeHeySequence?.currentDate
      )} ${props.MakeAppointmentMeHeySequence?.currentDate?.getFullYear()}`;
    }
  };

  let providerAppointments = useMemo(() => {
    return (props.MakeAppointmentMeHeySequence?.dsb || [])
      .flatMap((dsbItem: any) => {
        return dsbItem.providers;
      })
      .map((providerItem: any) => {
        return {
          ishaveAppointment: providerItem.dsb.appointments?.length > 0,
          availableSlots: props.isPackageAppointment ? getAvailableSlotCount(providerItem.dsb, availabilityNow) : undefined,
          doctor_dsb_id: providerItem.dsb.doctor_dsb_id,
          title: providerItem.dsb.doctor_provider_name,
          provider_id: providerItem.dsb.doctor_provider_id,
          doctor: providerItem.dsb.doctor_provider_name,
          startTime: adStringToDateObject(
            providerItem.dsb.date,
            providerItem.dsb.doctor_start_time
          ),
          endTime: adStringToDateObject(providerItem.dsb.date, providerItem.dsb.doctor_end_time),
          location: providerItem.division_name,
        };
      })
      .filter((appointment: AppointmentMeHey) => {
        if (
          props.MakeAppointmentMeHeySequence?.selectedProviderIdsList &&
          props.MakeAppointmentMeHeySequence?.selectedProviderIdsList.length > 0
        ) {
          return props.MakeAppointmentMeHeySequence?.selectedProviderIdsList.includes(
            appointment.provider_id
          );
        }
        return true;
      });
  }, [
    props.MakeAppointmentMeHeySequence?.selectedProviderIdsList,
    props.MakeAppointmentMeHeySequence?.dsb,
    props.isPackageAppointment,
    availabilityNow,
  ]);

  // Render left panel - Calendar view
  const leftPanel = (
    <div className="calendar-main" onWheel={handleWheel}>
      {/* Header */}
      <div
        className="calendar-toolbar"
        style={{ ...(props.isAppointmentList && { display: "none" }) }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2 className="calendar-title">ทำรายการนัดหมาย</h2>
          {props.MakeAppointmentMeHeySequence?.AppointmentLoading && (
            <div className="calendar-loading-bubble">กำลังโหลดข้อมูล...</div>
          )}
        </div>

        <div className="toolbar-controls">
          <Dropdown
            placeholder="เลือกทั้งหมด"
            selection
            options={providersOptions}
            multiple
            search
            clearable={true}
            value={props.MakeAppointmentMeHeySequence?.selectedProviderIdsList || []}
            onChange={async (e, { value }) => {
              await props.runSequence({
                sequence: "MakeAppointmentMeHey",
                action: "SET_PROVIDER_FILTER",
                selectedProviderIdsList: value,
              });
            }}
            className="clinic-dropdown"
          />
        </div>
      </div>

      {/* Navigation and View Controls */}
      <div className="calendar-controls">
        <div className="navigation-controls">
          <Button basic size="small" onClick={handleToday} className="today-button">
            Today
          </Button>

          <Button.Group size="small" basic className="nav-buttons">
            <Button icon="chevron left" onClick={handlePrevious} />
            <Button icon="chevron right" onClick={handleNext} />
          </Button.Group>

          <div className="current-month">
            {props.MakeAppointmentMeHeySequence?.currentDate && getDisplayMonth()}
          </div>
        </div>

        <div className="view-controls">
          <Button.Group size="small">
            <Button
              className={
                props.MakeAppointmentMeHeySequence?.viewType === "week"
                  ? "view-button-selected"
                  : "view-button-default"
              }
              onClick={() => setViewType("week")}
            >
              Week
            </Button>
            <Button
              className={
                props.MakeAppointmentMeHeySequence?.viewType === "month"
                  ? "view-button-selected"
                  : "view-button-default"
              }
              onClick={() => setViewType("month")}
            >
              Month
            </Button>
            <Button
              className={
                props.MakeAppointmentMeHeySequence?.viewType === "day"
                  ? "view-button-selected"
                  : "view-button-default"
              }
              onClick={() => setViewType("day")}
            >
              Day
            </Button>
          </Button.Group>
        </div>
      </div>

      {/* Calendar Views */}
      {props.MakeAppointmentMeHeySequence?.viewType === "month" && (
        <div className="calendar-content calendar-content-month">
          <AppointmentMonthViewMeHey
            currentDate={props.MakeAppointmentMeHeySequence?.currentDate}
            appointments={providerAppointments || []}
            onDayClick={handleDayClick}
            onAppointmentClick={handleAppointmentClick}
            selectedAppointmentId={selectedDoctorDSB?.doctor_dsb_id}
            showAvailability={props.isPackageAppointment}
            onMoreClick={handleMoreClick}
          />
        </div>
      )}
      {props.MakeAppointmentMeHeySequence?.viewType === "week" && (
        <div className="calendar-content calendar-content-week">
          <AppointmentWeekViewMeHey
            currentDate={props.MakeAppointmentMeHeySequence?.currentDate}
            appointments={providerAppointments || []}
            onAppointmentClick={handleAppointmentClick}
            selectedAppointmentId={selectedDoctorDSB?.doctor_dsb_id}
            onMoreClick={handleMoreClick}
          />
        </div>
      )}
      {props.MakeAppointmentMeHeySequence?.viewType === "day" && (
        <div className="calendar-content calendar-content-day">
          <AppointmentDayViewMeHey
            currentDate={props.MakeAppointmentMeHeySequence?.currentDate}
            appointments={providerAppointments || []}
            onAppointmentClick={handleAppointmentClick}
            selectedAppointmentId={selectedDoctorDSB?.doctor_dsb_id}
          />
        </div>
      )}
    </div>
  );

  // Render right panel - Appointment details
  const rightPanel = props.isPackageAppointment ? (
    <PackageRightPanelMeHey
      apiToken={props.controller?.apiToken}
      division={props.controller?.data?.division}
      setProp={props.setProp}
      providersOptions={providersOptions}
      open={props.MakeAppointmentMeHeySequence?.showCreateModal || false}
      onClose={handleCloseCreateModal}
      editingAppointment={editingAppointment}
      selectedDoctorDSB={selectedDoctorDSB}
      onEvent={props.onEvent}
      searchedItemListWithKey={props.searchedItemListWithKey}
      runSequence={props.runSequence}
      MakeAppointmentMeHeySequence={props.MakeAppointmentMeHeySequence}
      selectPackageBinder={props.selectPackageBinder}
      buttonLoadCheck={props.buttonLoadCheck}
      onAppointmentSuccess={props.onAppointmentSuccess}
      selectedPatient={props.selectedPatient}
    />
  ) : (
    <AppointmentRightPanelMeHey
      selectedDoctorDSB={selectedDoctorDSB}
      panelBackgroundColor={panelBackgroundColor}
      sortField={sortField}
      sortDirection={sortDirection}
      onSort={handleSort}
      runSequence={props.runSequence}
    />
  );

  return (
    <>
      {props.isAppointmentList ? (
        leftPanel
      ) : (
        <TwoPanelSplitView
          leftPanel={leftPanel}
          rightPanel={rightPanel}
          defaultLeftWidth={props.isPackageAppointment ? 64 : 72.5}
          minPanelWidth={40}
          maxPanelWidth={85}
          className="calendar-container"
        />
      )}

      {/* Create/Edit Appointment Modal */}
      <ModalCreateAppointmentMeHey
        providersOptions={providersOptions}
        open={props.MakeAppointmentMeHeySequence?.showCreateModal || false}
        onClose={handleCloseCreateModal}
        editingAppointment={editingAppointment}
        selectedDoctorDSB={selectedDoctorDSB}
        onEvent={props.onEvent}
        searchedItemListWithKey={props.searchedItemListWithKey}
        runSequence={props.runSequence}
        MakeAppointmentMeHeySequence={props.MakeAppointmentMeHeySequence}
      />
    </>
  );
};

export default CardMakeAppointmentMeHey;
